import { ArrowDownRight, ArrowUpRight, Minus, Star } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import type { DeltaTone } from "./executiveMetrics";

// Small, dependency-free building blocks for the executive dashboard. No chart
// library: the shapes here (bars, stars, rows) are a few divs each, render
// instantly and follow the theme tokens in both colour schemes.

export const DeltaChip = ({
  label,
  tone,
  hint,
}: {
  label: string;
  tone: DeltaTone;
  hint: string;
}) => {
  const Icon =
    tone === "neutral"
      ? Minus
      : label.startsWith("−")
        ? ArrowDownRight
        : ArrowUpRight;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-xs font-medium tabular-nums",
        tone === "positive" &&
          "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
        tone === "negative" &&
          "bg-rose-500/10 text-rose-700 dark:text-rose-400",
        tone === "neutral" && "bg-muted text-muted-foreground",
      )}
      title={hint}
    >
      <Icon className="size-3" aria-hidden="true" />
      {label}
      <span className="sr-only"> {hint}</span>
    </span>
  );
};

export const Unavailable = ({ error }: { error?: unknown }) => (
  <span
    className="text-sm text-muted-foreground"
    title={error instanceof Error ? error.message : undefined}
  >
    Unavailable
  </span>
);

const STAT_TILE_CLASS =
  "group flex min-h-32 flex-col justify-between rounded-xl border bg-card p-4 text-card-foreground shadow-xs";

/**
 * One headline number. With `to`, the whole tile is the link so a CEO on a
 * phone can tap anywhere on it to drill into the page that explains the
 * figure; without it the tile is a plain region (the page is the detail).
 * `info` sits beside the label and may hold its own control (a definition
 * tooltip), which is why a linked tile should not use it.
 */
export const StatTile = ({
  label,
  icon,
  value,
  delta,
  sublabel,
  to,
  info,
  isPending,
  error,
  children,
}: {
  label: string;
  icon: ReactNode;
  value: ReactNode;
  delta?: ReactNode;
  sublabel?: ReactNode;
  to?: string;
  info?: ReactNode;
  isPending: boolean;
  error?: unknown;
  children?: ReactNode;
}) => {
  const body = (
    <StatTileBody
      label={label}
      icon={icon}
      value={value}
      delta={delta}
      sublabel={sublabel}
      info={info}
      isPending={isPending}
      error={error}
    >
      {children}
    </StatTileBody>
  );
  if (!to)
    return (
      <div role="group" aria-label={label} className={STAT_TILE_CLASS}>
        {body}
      </div>
    );
  return (
    <Link
      to={to}
      className={cn(
        STAT_TILE_CLASS,
        "transition-colors hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
      )}
    >
      {body}
    </Link>
  );
};

const StatTileBody = ({
  label,
  icon,
  value,
  delta,
  sublabel,
  info,
  isPending,
  error,
  children,
}: {
  label: string;
  icon: ReactNode;
  value: ReactNode;
  delta?: ReactNode;
  sublabel?: ReactNode;
  info?: ReactNode;
  isPending: boolean;
  error?: unknown;
  children?: ReactNode;
}) => (
  <>
    <div className="flex items-center justify-between gap-2">
      <span className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
        {info}
      </span>
      <span className="text-muted-foreground" aria-hidden="true">
        {icon}
      </span>
    </div>
    {isPending ? (
      <div className="space-y-2 pt-2">
        <Skeleton className="h-8 w-24" />
        <Skeleton className="h-3 w-32" />
      </div>
    ) : error ? (
      <div className="pt-2">
        <Unavailable error={error} />
      </div>
    ) : (
      <div className="space-y-1 pt-2">
        <div className="flex flex-wrap items-end gap-2">
          <span className="text-3xl font-semibold leading-none tracking-tight tabular-nums">
            {value}
          </span>
          {delta}
        </div>
        {children}
        {sublabel ? (
          <p className="text-xs text-muted-foreground">{sublabel}</p>
        ) : null}
      </div>
    )}
  </>
);

/** Five stars filled to the average, e.g. 4.3 → four full stars and a third. */
export const StarMeter = ({
  rating,
  className,
}: {
  rating: number | null;
  className?: string;
}) => {
  const filledPercent =
    rating == null ? 0 : Math.max(0, Math.min(100, (rating / 5) * 100));
  return (
    <span
      className={cn("relative inline-block leading-none", className)}
      role="img"
      aria-label={
        rating == null ? "No ratings yet" : `${rating} out of 5 stars`
      }
    >
      <span className="flex gap-0.5 text-muted-foreground/30">
        {[0, 1, 2, 3, 4].map((index) => (
          <Star
            key={index}
            className="size-4 fill-current"
            aria-hidden="true"
          />
        ))}
      </span>
      <span
        className="absolute inset-y-0 left-0 flex gap-0.5 overflow-hidden text-amber-500 dark:text-amber-400"
        style={{ width: `${filledPercent}%` }}
      >
        {[0, 1, 2, 3, 4].map((index) => (
          <Star
            key={index}
            className="size-4 shrink-0 fill-current"
            aria-hidden="true"
          />
        ))}
      </span>
    </span>
  );
};

export type BarColumn = {
  key: string;
  value: number;
  label: string;
  title: string;
  emphasis: "current" | "baseline" | "today" | "future";
};

/** Fourteen thin columns, this week in the foreground colour. */
export const BarColumns = ({
  columns,
  ariaLabel,
}: {
  columns: readonly BarColumn[];
  ariaLabel: string;
}) => {
  const max = Math.max(1, ...columns.map((column) => column.value));
  return (
    <div
      className="flex h-40 items-end gap-1.5"
      role="img"
      aria-label={ariaLabel}
    >
      {columns.map((column) => (
        <div
          key={column.key}
          className="flex h-full min-w-0 flex-1 flex-col items-center justify-end gap-1"
          title={column.title}
        >
          <span className="text-[10px] font-medium tabular-nums text-muted-foreground">
            {column.value > 0 ? column.value : ""}
          </span>
          <div
            className={cn(
              "w-full rounded-t-sm transition-[height]",
              column.emphasis === "current" && "bg-primary",
              column.emphasis === "today" &&
                "bg-primary ring-2 ring-primary/30",
              column.emphasis === "baseline" && "bg-muted-foreground/30",
              column.emphasis === "future" &&
                "border border-dashed border-border bg-transparent",
            )}
            style={{
              height: `${column.value > 0 ? Math.max(4, (column.value / max) * 100) : column.emphasis === "future" ? 4 : 2}%`,
            }}
          />
          <span
            className={cn(
              "text-[10px] uppercase",
              column.emphasis === "today"
                ? "font-semibold text-foreground"
                : "text-muted-foreground",
            )}
          >
            {column.label}
          </span>
        </div>
      ))}
    </div>
  );
};

export type DistributionRow = {
  key: string;
  label: ReactNode;
  count: number;
  detail?: string;
  tone?: "default" | "warning";
};

/** Horizontal bars scaled to the largest row: rating buckets, occasions. */
export const DistributionRows = ({
  rows,
  wideLabels = false,
}: {
  rows: readonly DistributionRow[];
  /** Room for longer labels (funnel steps) before they truncate. */
  wideLabels?: boolean;
}) => {
  const max = Math.max(1, ...rows.map((row) => row.count));
  return (
    <ul className="space-y-2">
      {rows.map((row) => (
        <li
          key={row.key}
          className={cn(
            "grid items-center gap-3 text-sm",
            wideLabels
              ? "grid-cols-[minmax(0,11rem)_1fr_auto]"
              : "grid-cols-[minmax(0,6rem)_1fr_auto]",
          )}
        >
          <span className="truncate text-muted-foreground">{row.label}</span>
          <span className="h-2 overflow-hidden rounded-full bg-muted">
            <span
              className={cn(
                "block h-full rounded-full",
                row.tone === "warning"
                  ? "bg-amber-500 dark:bg-amber-400"
                  : "bg-primary",
              )}
              style={{ width: `${(row.count / max) * 100}%` }}
            />
          </span>
          <span className="text-right tabular-nums">
            {row.count}
            {row.detail ? (
              <span className="ml-1 text-xs text-muted-foreground">
                {row.detail}
              </span>
            ) : null}
          </span>
        </li>
      ))}
    </ul>
  );
};

export const SectionCard = ({
  title,
  icon,
  action,
  children,
  className,
}: {
  title: string;
  icon: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) => (
  <Card className={cn("gap-4", className)}>
    <CardHeader className="flex flex-row items-center justify-between gap-2">
      <CardTitle className="flex items-center gap-2 text-base">
        <span className="text-muted-foreground" aria-hidden="true">
          {icon}
        </span>
        {title}
      </CardTitle>
      {action}
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

export const CardLink = ({
  to,
  children,
}: {
  to: string;
  children: ReactNode;
}) => (
  <Link
    to={to}
    className="text-xs font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
  >
    {children}
  </Link>
);

export const CardSkeleton = ({ rows = 4 }: { rows?: number }) => (
  <div className="space-y-3">
    {Array.from({ length: rows }, (_, index) => (
      <Skeleton
        key={index}
        className={cn("h-4", index % 2 ? "w-2/3" : "w-full")}
      />
    ))}
  </div>
);
